import { useState } from "react";
import { AxiosError } from "axios";

import { CardInputsContainer, ConfirmActionModal } from "~/common/components";
import { PlusIcon } from "~/common/components/icons";
import { PharmacyBoxLayout } from "~/common/components/PharmacyBoxLayout";
import {
  PharmacyDeleteErrorResponse,
  useDeletePatientPharmacy,
  useGetPatientPharmacies,
} from "~/common/hooks/queries/pharmacy";
import { PharmacyQueryType } from "~/common/types/pharmacies";
import { PharmacyCard } from "./PharmacyCard";
import { PharmacyModalForm } from "./PharmacyModalForm";

interface PatientPharmaciesProps {
  className?: string;
  patientId?: string;
  title?: string;
  canEdit?: boolean;
}

export const PatientPharmacies = ({
  patientId,
  className,
  title = patientId ? "Pharmacies" : "My pharmacies",
  canEdit = !patientId,
}: PatientPharmaciesProps) => {
  const [errorModalOpen, setErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [pharmacyFormOpen, setPharmacyFormOpen] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState<
    PharmacyQueryType | undefined
  >(undefined);
  const [pharmacyIdToDelete, setPharmacyIdToDelete] = useState<string>();

  const onDeleteClose = () => setPharmacyIdToDelete(undefined);
  const onErrorClose = () => setErrorModalOpen((prev) => !prev);
  const onError = (error: AxiosError<PharmacyDeleteErrorResponse>) => {
    setErrorModalOpen(true);
    setErrorMessage(
      `You ${
        error.response?.data?.detail.toLowerCase() ?? "An error occurred"
      }.`,
    );
  };

  const onPharmacyFormClose = () => {
    setPharmacyFormOpen(false);

    setTimeout(() => {
      setSelectedPharmacy(undefined);
    }, 300);
  };

  const { data: patientPharmacies } = useGetPatientPharmacies({ patientId });

  const hasDefaultPharmacy =
    patientPharmacies?.some((pharmacy) => pharmacy.is_default) ?? false;

  const { deletePharmacy, isPending } = useDeletePatientPharmacy(
    onDeleteClose,
    onError,
    patientId,
  );

  return (
    <>
      <CardInputsContainer
        className={className}
        contentClassName="pt-1 pb-5"
        title={title}
      >
        <div className="@container">
          <div className="grid w-full grid-cols-1 gap-4 @xl:grid-cols-3 @4xl:grid-cols-4">
            {patientPharmacies?.map((patientPharmacy) => (
              <PharmacyCard
                patientPharmacy={patientPharmacy}
                key={patientPharmacy.id}
                patientId={patientId}
                canEdit={canEdit}
                setPharmacyIdToDelete={setPharmacyIdToDelete}
                setPharmacyForm={(pharmacy) => {
                  setSelectedPharmacy(pharmacy);
                  setPharmacyFormOpen(true);
                }}
              />
            ))}

            {canEdit && (
              <PharmacyBoxLayout>
                <button
                  type="button"
                  className="flex h-full flex-col items-center justify-center"
                  onClick={() => {
                    setPharmacyFormOpen(true);
                  }}
                >
                  <div className="rounded-full bg-gray-100 p-2">
                    <PlusIcon className="bg-gray-100 text-gray-600" />
                  </div>
                  <p className="text-base font-medium text-gray-70">
                    Add new pharmacy
                  </p>
                </button>
              </PharmacyBoxLayout>
            )}
          </div>
        </div>
      </CardInputsContainer>
      <ConfirmActionModal
        cancelButtonClassName="w-32 self-end"
        confirmButtonClassName="w-32 self-end"
        title="Delete pharmacy"
        description="Are you sure you want to delete this pharmacy?"
        confirmButtonText="Delete"
        show={!!pharmacyIdToDelete}
        isLoading={isPending}
        onClose={onDeleteClose}
        onConfirm={() => {
          deletePharmacy(pharmacyIdToDelete ?? "");
        }}
      />
      <ConfirmActionModal
        title="Error"
        description={errorMessage}
        show={errorModalOpen}
        confirmButtonText="Ok"
        onClose={onErrorClose}
        cancelButtonClassName="hidden"
        onConfirm={onErrorClose}
      />
      {canEdit && (
        <PharmacyModalForm
          show={pharmacyFormOpen}
          onClose={onPharmacyFormClose}
          selectedPharmacy={selectedPharmacy}
          hasDefaultPharmacy={hasDefaultPharmacy}
          patientId={patientId}
        />
      )}
    </>
  );
};
